import type { Metadata } from 'next'
import { SITE, absoluteUrl } from '@/lib/site'

/**
 * The brand suffix the root layout's `title.template` appends. Kept here so
 * length budgeting and the metadata report agree on one number.
 */
export const BRAND_SUFFIX = ` | ${SITE.name}`

/**
 * Google truncates titles around 580px, which is roughly 60 characters. That
 * budget covers the *rendered* title — the page's own string plus the suffix.
 */
export const TITLE_MAX = 60
export const DESCRIPTION_MAX = 155

export type PageMetadataInput = {
  /** Site-relative path, e.g. '/services/seo'. Drives the self-referencing canonical. */
  path: string
  /** The page's own title. The root template appends the brand unless `absoluteTitle`. */
  title: string
  description: string
  /**
   * Skip the brand template — the title is used verbatim. Use when the title
   * already contains the brand (the homepage) or is long enough that a suffix
   * would push it past the truncation point.
   */
  absoluteTitle?: boolean
  /** Absolute or site-relative OG image. Falls back to the route's opengraph-image. */
  image?: string
  /** Alt text for `image`. Ignored when `image` is unset. */
  imageAlt?: string
  /** og:type — 'website' for evergreen pages, 'article' for editorial. */
  type?: 'website' | 'article'
  noindex?: boolean
}

/**
 * Build a complete `Metadata` object for a statically-defined page.
 *
 * Every page in the app should go through here rather than hand-rolling a
 * literal. Hand-rolled objects are why canonicals, per-page OG tags, and title
 * length discipline drifted — nothing enforced them.
 *
 * Two things here are deliberate and easy to undo by accident:
 *
 * 1. It never sets `openGraph.title`. Next resolves og:title from the templated
 *    `title`, so leaving it alone is what keeps og:title and `<title>`
 *    identical. Setting it explicitly is exactly how they used to diverge.
 *
 * 2. It omits the `openGraph` / `twitter` objects entirely unless the page
 *    supplies its own image. An explicit `openGraph` object REPLACES the one
 *    resolved from parent segments rather than merging into it — which drops
 *    the `app/(site)/opengraph-image.tsx` card, and `og:locale` with it. Next
 *    already derives og:description and twitter:description from `description`,
 *    so omitting the objects gives strictly better output. The cost is `og:url`,
 *    which is redundant with the canonical we always emit.
 */
export function pageMetadata(input: PageMetadataInput): Metadata {
  const { path, title, description, absoluteTitle, image, imageAlt, type = 'website', noindex } = input

  const canonical = absoluteUrl(path)

  // Only when the page overrides the site-wide social card — see note 2 above.
  const social = image
    ? {
        openGraph: {
          description,
          url: canonical,
          type,
          images: [{ url: absoluteUrl(image), alt: imageAlt ?? title }],
        },
        twitter: {
          description,
          images: [absoluteUrl(image)],
        },
      }
    : {}

  return {
    title: absoluteTitle ? { absolute: title } : title,
    description,
    alternates: { canonical },
    ...social,
    // `noindex, follow`, never nofollow. Keeping a page out of the index is a
    // ranking decision; refusing to follow its links is a crawl decision, and
    // on your own site it is almost always wrong — it strands whatever the page
    // links to. /learn/series/* is noindexed precisely so it can keep passing
    // crawlers through to otherwise-orphaned posts.
    ...(noindex ? { robots: { index: false, follow: true } } : {}),
  }
}

/**
 * The rendered length of a title once the brand template is applied. Used by
 * the metadata report (`npm run seo:report`) to flag pages over budget.
 */
export function renderedTitleLength(title: string, absoluteTitle = false): number {
  return absoluteTitle ? title.length : title.length + BRAND_SUFFIX.length
}
